import { Link } from "react-router-dom";
import { motion } from "framer-motion";
import { ArrowLeft } from "lucide-react";
import { LogoFull } from "@/components/Logo";

const Section = ({ title, children }) => (
  <section className="space-y-3">
    <h2 className="font-display font-bold text-xl text-cyan-300">{title}</h2>
    <div className="text-slate-400 text-sm leading-relaxed space-y-3">{children}</div>
  </section>
);

export default function Terms() {
  return (
    <main className="min-h-screen bg-[#06090E] grid-bg" data-testid="terms-page">
      <header className="border-b border-white/[0.08] bg-slate-100/95 backdrop-blur-xl">
        <div className="max-w-4xl mx-auto px-5 sm:px-8 h-[76px] flex items-center justify-between">
          <Link to="/" data-testid="terms-logo"><LogoFull /></Link>
          <Link to="/" data-testid="terms-back-link" className="flex items-center gap-2 text-sm text-slate-600 hover:text-slate-900 transition-colors">
            <ArrowLeft size={16} /> Til forsiden
          </Link>
        </div>
      </header>

      <motion.div
        initial={{ opacity: 0, y: 24 }}
        animate={{ opacity: 1, y: 0 }}
        transition={{ duration: 0.7, ease: [0.16, 1, 0.3, 1] }}
        className="max-w-4xl mx-auto px-5 sm:px-8 py-14"
      >
        <p className="mono text-xs tracking-[0.3em] uppercase text-cyan-400 mb-3">Juridisk</p>
        <h1 className="font-display font-extrabold tracking-tight text-3xl sm:text-4xl mb-4" data-testid="terms-heading">
          Vilkår og betingelser
        </h1>
        <p className="text-slate-500 text-sm mb-12">Sist oppdatert: oktober 2026 · Gjelder kun kunder i Norge</p>

        <div className="space-y-10">
          <Section title="1. Generelt">
            <p>
              Disse vilkårene gjelder for produkter og tjenester levert til privatkunder («kunden») av CLSF AS
              («selger»), med mindre annet er skriftlig avtalt mellom partene. Vi selger kun til kunder med
              adresse i Norge. For bedriftskunder gjelder egne vilkår som fås ved henvendelse via kontaktskjemaet
              på nettsiden.
            </p>
            <p>
              Forbrukerkjøpsloven, angrerettloven og annen relevant norsk lovgivning gjelder for alt som ikke er
              dekket av disse vilkårene.
            </p>
          </Section>

          <Section title="2. Parter">
            <p>
              <strong className="text-slate-200">Selger:</strong> CLSF AS, org.nr. 923 936 076, Buråsen 17,
              4636 Kristiansand, Norge. Kontakt skjer via kontaktskjemaet på nettsiden.
            </p>
            <p>
              <strong className="text-slate-200">Kunde:</strong> Personen som er registrert på bestillingen.
              Kunden må være over 18 år, ikke handle på vegne av en virksomhet, og ikke ha til hensikt å bruke
              produktet eller tjenesten i yrkessammenheng. Samme person er mottaker av ordrebekreftelse,
              forsendelse og faktura.
            </p>
          </Section>

          <Section title="3. Kommunikasjon">
            <p>
              E-post brukes som primær kommunikasjonskanal for ordrebekreftelse, sporingsinformasjon, faktura,
              inkassovarsel, prisendringer og annen viktig informasjon. Kunden er selv ansvarlig for filtre i
              sitt eget e-postsystem som kan sortere bort eller slette slike e-poster.
            </p>
          </Section>

          <Section title="4. Priser">
            <p>
              Prisen er lik for alle kunder i Norge. Vi viser ikke personaliserte priser basert på
              informasjonskapsler, brukerprofilering eller automatiserte prisroboter.
            </p>
            <p>
              For å bruke tjenesten må kunden kjøpe en fysisk sporingsenhet og registrere et betalt abonnement.
              Bruk av SMS ut over det som inngår belastes i tillegg. Kunden er ansvarlig for SMS som sendes til
              eller fra sporingsenheten, også SMS som skyldes feil eller feilberegninger fra enheten eller
              programvaren. SMS- og e-postvarsler kan enkelt slås av og på i brukergrensesnittet.
            </p>
            <p>
              Prisendringer varsles til kunden minst 30 dager før de trer i kraft. For forhåndsbetalte tjenester
              gjelder den opprinnelige prisen for hele inneværende avtaleperiode.
            </p>
          </Section>

          <Section title="5. Leveranse">
            <p>
              Produktene sendes fra Norge. Selger bærer risikoen og ansvaret frem til forsendelsen er levert til
              eller hentet av kunden, overtatt av kundens representant, eller levert på et sted kunden aktivt har
              valgt (for eksempel postkasse, nabo eller arbeidsplass).
            </p>
            <p>
              Dersom en forsendelse ikke hentes, eller er umulig å levere basert på opplysningene kunden har
              oppgitt, kan selger kreve dekket sine faktiske utgifter, maksimalt tilsvarende kostnaden ved å
              levere varen til kunden.
            </p>
            <p>
              Selger er ikke forpliktet til å levere dersom det har oppstått en skrivefeil, og kunden innså eller
              burde innsett at det forelå en slik feil, eller dersom selger ville lide vesentlig tap ved å
              gjennomføre bestillingen.
            </p>
          </Section>

          <Section title="6. Betaling">
            <p>
              Selger sender faktura (eFaktura) med 7 dagers betalingsfrist fra leveringsdato. Dersom betalingen
              er mer enn 14 dager forsinket, sendes en betalingspåminnelse. Dersom fakturaen fortsatt ikke er
              betalt innen ytterligere 14 dager, sendes inkassovarsel med informasjon om at kravet overføres til
              inkasso.
            </p>
            <p>
              Ved forsinket betaling kan selger kreve kr 35,- i gebyr per purring/inkassovarsel og 11,75 %
              forsinkelsesrente fra forfall, uavhengig av merkostnader som påløper dersom kravet sendes til
              inkasso. Alle tjenester stanses etter 28 dagers betalingsforsinkelse, og kundens forpliktelser
              anses da som misligholdt. Har kunden et abonnement, iverksettes oppsigelsesprosess med bruddgebyr
              som beskrevet i punktet om abonnement. Feil eller mangler som ikke skyldes selger, fritar ikke
              kunden fra plikten til å betale.
            </p>
          </Section>

          <Section title="7. Angrerett og retur">
            <p>
              Kunden kan angre kjøpet innen 14 dager, og vi refunderer alle betalinger vi har mottatt, inkludert
              leveringskostnader (unntatt tilleggskostnader ved valg av annen leveringsmåte enn vår billigste
              standardlevering), uten unødig forsinkelse og senest 14 dager etter at vi mottar meldingen eller
              returen. Melding om bruk av angreretten må gis skriftlig innen 14 dager, og varen må være oss i
              hende senest 28 dager etter at kunden mottok den. Meldingen kan sendes på papir sammen med returen
              eller via kontaktskjemaet på nettsiden.
            </p>
            <p>
              Kunden bærer risikoen og kostnaden for returen. Kunden har rett til å undersøke varen slik man kan
              i en fysisk butikk for å avgjøre om angreretten skal benyttes. Kunden kan likevel holdes ansvarlig
              for redusert verdi dersom undersøkelsen går utover det som er rimelig og nødvendig. For full
              refusjon må varen kunne selges som ny uten vesentlig tap.
            </p>
            <p>
              Å nekte å motta pakken, eller la den bli stående på hentestedet, regnes ikke som bruk av
              angreretten. Uhentede forsendelser kan medføre dekning av selgers faktiske utgifter, inkludert
              returfrakt og et rimelig gebyr. Returforsendelser må leveres til selgers adresse; forsendelser
              levert til hentesteder blir ikke hentet.
            </p>
            <p>
              Unntak fra angreretten er varer eller tjenester som er brukt opp, varer som ikke kan returneres, og
              varer som er spesialtilpasset kundens behov.
            </p>
          </Section>

          <Section title="8. Reklamasjonsrett">
            <p>
              Reklamasjonsretten er 5 år for sporingsenheter og 2 år for batterier, også når batteriet er
              integrert i enheten. Produktene må brukes, vedlikeholdes og lagres slik det er rimelig å forvente
              for å beholde reklamasjonsretten.
            </p>
            <p>
              Selger er kun ansvarlig for tap ved feil eller mangler som påvises å ligge på selgers side, med
              mindre selger godtgjør at mangelen skyldes forhold utenfor selgers kontroll som ikke med rimelighet
              kunne unngås eller overvinnes. Selger er ikke ansvarlig for indirekte tap, med mindre tapet skyldes
              grov uaktsomhet eller forsett. Eksempler på forhold utenfor selgers kontroll:
            </p>
            <ul className="list-disc list-inside space-y-1 pl-2">
              <li>Manglende normalt vedlikehold</li>
              <li>Fortsatt bruk etter at feil eller for store belastninger er eller burde vært oppdaget</li>
              <li>Manglende rutinemessige tilstandskontroller</li>
              <li>Manglende søking av hjelp eller veiledning</li>
            </ul>
            <p>
              Selger utsteder ingen egne garantier, men viderefører garantier utstedt av andre så lenge produktet
              er kjøpt fra selger.
            </p>
          </Section>

          <Section title="9. Abonnement">
            <p>
              Abonnementet fornyes automatisk med mindre kunden gir skriftlig beskjed til selger minst 30 dager
              før utløpet av gjeldende abonnementsperiode. Oppsigelse skjer via kontaktskjemaet på nettsiden.
            </p>
            <p>
              Ved oppsigelse av et abonnement med bindingstid over flere perioder, betales et bruddgebyr på 70 %
              av gjenværende verdi. Abonnementet avsluttes da med én samlefaktura for alle gjenværende perioder.
              Eksempel: Er det 6 måneder igjen av et abonnement til 50 kr per måned, er gjenværende verdi 300 kr,
              og bruddgebyret blir 210 kr.
            </p>
          </Section>

          <Section title="10. Brukerstøtte">
            <p>
              Selger tilbyr gratis brukerstøtte for produkter og tjenester solgt av selger. Der feil eller mangler
              er forårsaket av kunden eller en tredjepart, kan selger avstå fra å gi støtte, eller belaste 350 kr
              per time for slik bistand, forutsatt at kunden godtar dette før arbeidet starter. Eksempler:
            </p>
            <ul className="list-disc list-inside space-y-1 pl-2">
              <li>Produktet er brukt til andre formål enn beskrevet i produktbeskrivelsen</li>
              <li>Produktet er installert av tredjepart, og feilen skyldes installasjonen</li>
              <li>Komponenter er byttet med inkompatible deler</li>
              <li>Produktet er brukt utenfor angitte verdier for temperatur og fuktighet</li>
              <li>Produktet brukes på steder uten tilstrekkelig signalstyrke</li>
            </ul>
          </Section>

          <Section title="11. Ansvar">
            <p>
              Selger er ikke ansvarlig for direkte, indirekte eller tilfeldige skader, tap, forstyrrelser eller
              funksjonsfeil som oppstår ved bruk av solgte produkter eller tjenester. Kunden har eneansvaret for
              å kontrollere at enheten eller kjøretøyet er kompatibelt med produktet, og at bruken er trygg og
              lovlig. Uttalelser fra selger skal ikke brukes som konklusjon om kompatibilitet, sikkerhet eller
              lovlighet – kunden må innhente informasjon fra egnede kilder og selv konkludere.
            </p>
          </Section>

          <Section title="12. Uautorisert bruk">
            <p>
              Produktets deksel, hus eller forsegling må ikke åpnes – reklamasjonsretten bortfaller og støtte gis
              ikke dersom produktet er åpnet. SIM-kortet må ikke fjernes eller erstattes. Ikke bruk inkompatible
              produkter, utstyr eller deler. Forsøk på å få tilgang til produktene – herunder revers engineering,
              demontering, hacking eller sending av SMS-kommandoer – er ikke tillatt. Kunden kan holdes ansvarlig
              for kostnader eller forpliktelser selger pådras som følge av åpenbart uaktsom og uautorisert bruk.
            </p>
          </Section>

          <Section title="13. Personvern">
            <p>
              Kunden kan når som helst kontakte selger via kontaktskjemaet for innsyn i hvilke personopplysninger
              som er lagret, for retting eller for sletting. Selger har tilgang til kundedata for støtte,
              fakturering og vedlikehold.
            </p>
            <p>
              <strong className="text-slate-200">Opplysninger som samles inn:</strong> Navn og adresse
              (forsendelse, betaling og eierskap), telefonnummer og e-postadresse (kommunikasjon om
              ordrebekreftelse, forsendelse, betaling og viktig informasjon).
            </p>
            <p>
              <strong className="text-slate-200">Underleverandører som kan få tilgang:</strong> nettbutikk
              (bestilling), fraktleverandør (forsendelse), betalingsleverandør (betaling), regnskapsfører og
              revisor (myndighetskrav), sporingsserver/nettside (innlogging og sporing) og
              mobilnettoperatører (kommunikasjon mellom sporingsenhet og servere).
            </p>
          </Section>

          <Section title="14. Force majeure">
            <p>
              Ingen av partene holdes ansvarlig overfor den andre for svikt eller forsinkelse i oppfyllelsen av
              avtalen når dette skyldes forhold utenfor partens rimelige kontroll, herunder brann, flom,
              jordskjelv, embargo, mangel, epidemi, karantene, krig, terrorhandlinger, opprør, streik, lockout,
              offentlige pålegg eller endringer i infrastruktur som gjør enheter foreldet.
            </p>
          </Section>

          <Section title="15. Endringer i vilkårene">
            <p>
              Selger kan endre gjeldende vilkår når som helst. Vesentlige endringer publiseres med krav om ny
              aksept fra kunden. Ubetydelige endringer kan gjøres uten varsel. Selger kan stanse alle tjenester
              dersom kunden avviser eller ikke svarer på oppfordringen om å godta nye vilkår.
            </p>
          </Section>

          <Section title="16. Kopier og lagre">
            <p>
              Kunden bør kopiere og lagre gjeldende versjon av vilkårene fra kjøpstidspunktet dersom det er
              ønskelig som referanse. Dersom kunden ikke har gjort dette, benyttes selgers kopi ved eventuelle
              senere behov.
            </p>
          </Section>
        </div>
      </motion.div>
    </main>
  );
}
